import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { BulkUpsertCostcentersAssignmentAssignmentsToUpdateInput } from 'generated/resources/datatable.graphql'
import {
  BulkDeleteCostCenterAssignmentsDocument,
  BulkUpsertCostCenterAssignmentDocument,
  BulkUpsertCostcentersAssignmentAssignmentsToCreateInput,
} from 'generated/resources/graphql'
import { noop } from 'lodash'

import { useMutation } from 'modules/core/hooks/useMutation'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface CreateProps {
  assignments: BulkUpsertCostcentersAssignmentAssignmentsToCreateInput[]
}

interface SaveProps {
  assignmentsToCreate: BulkUpsertCostcentersAssignmentAssignmentsToCreateInput[]
  assignmentsToUpdate: BulkUpsertCostcentersAssignmentAssignmentsToUpdateInput[]
}

interface DeleteProps {
  ids: string[]
}

export const useCostCentersMutations = (refetchQueries?: TypedDocumentNode[]) => {
  const [bulkDeleteCostCenterAssignment] = useMutation(BulkDeleteCostCenterAssignmentsDocument, {
    extractorFn: (newData) => newData.costCentersMutations.bulkDeleteCostcentersAssignment,
    ...(refetchQueries && { refetchQueries }),
  })

  const [bulkUpsertCostCenterAssignment] = useMutation(BulkUpsertCostCenterAssignmentDocument, {
    extractorFn: (newData) => newData.costCentersMutations.bulkUpsertCostcentersAssignment,
    ...(refetchQueries && { refetchQueries }),
  })

  const manageAction = (response: UnhandledResult<Result<unknown>>, successMessage?: string) => {
    const result = handleResult(response, noop)

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: successMessage || i18n.t('finance.cost_center_detail.sidepanel.success'),
      })
    }

    return result.success
  }

  const create = async ({ assignments }: CreateProps) => {
    const response = await bulkUpsertCostCenterAssignment({
      variables: {
        assignmentsToCreate: assignments,
        assignmentsToUpdate: [],
      },
    })

    return manageAction(response, i18n.t('finance.cost_centers.assignments.create.success'))
  }

  const save = async ({ assignmentsToCreate, assignmentsToUpdate }: SaveProps) => {
    const response = await bulkUpsertCostCenterAssignment({
      variables: {
        assignmentsToCreate: assignmentsToCreate,
        assignmentsToUpdate: assignmentsToUpdate,
      },
    })

    return manageAction(response)
  }

  const deleteAssignments = async ({ ids }: DeleteProps) => {
    const response = await bulkDeleteCostCenterAssignment({
      variables: { ids },
    })

    return manageAction(response, i18n.t('finance.cost_centers.assignments.delete_modal.success'))
  }

  return {
    create,
    save,
    deleteAssignments,
  }
}
